// Squeegee's full backup (Settings, Data, Manage Backups, Download: a .sqg file; Liam 2 Oct 2026: "The
// optimal is [it's] opened and imported itself"). Its data file is every stored object in one list
// (docs/import-jev/squeegee/README.md). This turns the customers in it into the rows Squeegee's own
// "All job data" export (All_Jobs.csv) gives, one per job under the same headings, so the import goes
// on exactly as for that file: squeegeeJobs puts each customer's jobs together, buildCustomers reads
// them, and each Next Due from today on is booked. What the backup has that the CSV does not:
//   the customer's Description box (gate codes, how to get in), as their Job notes;
//   each job's Next Due worked out the way Squeegee works it out (below), never guessed.
// readBackupFile opens the file (seven-zip.ts, with the unpacker the import screen passes in); the rest
// are pure functions.
//
// Squeegee's own values, read from its web app (sqgee.com, 2 Oct 2026):
//   frequencyType: 0 ad hoc ("One-off"), 1 every N days, 2 every N weeks (on frequencyDayOfWeek, 1 Monday
//   to 7 Sunday), 3 every N months on the same date (frequencyDayOfMonth), 4 every N months on a week of
//   the month (frequencyWeekOfMonth: 0 first to 3 fourth, 5 last), 5 an outside calendar. frequencyRange
//   above 0 makes it "every N-range to N+range". An interval of 0 is ad hoc.
//   A job's `date` is the first date of its schedule; its Next Due is the first date on the schedule
//   from today on (Squeegee's getJobNextOnScheduleDueDate).
//   Customer and job `state`: "inactive" is stopped; anything else, or none, is active.
//   A job occurrence (resourceType "joboccurrences": jobId, status 1 = done, isoCompletedDate) is a
//   visit; the latest done one is the job's Last Done.

export const BACKUP_HEADINGS = ['Job Ref', 'Customer Ref', 'Name', 'Address', 'Phone', 'Email', 'Job Address', 'Job notes', 'Services', 'Frequency', 'Price', 'Last Done', 'Next Due', 'Notes'] as const

export interface BackupSheet {
  // The headings (Last Done only when some job has one), then one row per job (a customer with no jobs:
  // one row with no job details).
  rows: string[][]
  // Customers who are not coming in, and why, in words for the owner.
  notIn: { name: string; reason: string }[]
}

import { readSevenZip, type Unxz } from './seven-zip'

// What the owner is told when a backup can't be opened, whatever the reason: what to do instead.
export const BACKUP_UNREADABLE = "Roundly couldn't open that Squeegee backup. Send it to us below with \"Choose the file to send\" and we'll put your customers in for you, or choose Squeegee's All_Jobs.csv instead."

export class BackupError extends Error {}

type Obj = Record<string, unknown>
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v)
const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' && Number.isFinite(v) ? String(v) : '')
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : null)
const gone = (o: Obj): boolean => o._deleted === true
const stopped = (o: Obj): boolean => o.state === 'inactive'
const archived = (o: Obj): boolean => o._archived === true

// ---- dates (whole days, UTC) ----------------------------------------------------------------
const DAY = 86_400_000
const ISO = /^(\d{4})-(\d{2})-(\d{2})/
// A real calendar date in 2000 to 2100 (a date like 31 Feb, or year 9999, is none).
function dayOf(iso: string): number | null {
  const m = iso.match(ISO)
  if (!m || Number(m[1]) < 2000 || Number(m[1]) > 2100) return null
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = new Date(t)
  return d.getUTCMonth() === Number(m[2]) - 1 ? t / DAY : null
}
const isoOf = (day: number): string => new Date(day * DAY).toISOString().slice(0, 10)
const weekday = (day: number): number => ((new Date(day * DAY).getUTCDay() + 6) % 7) + 1
// The same day of the month N months on, or the month's last day (moment's add(N, 'months')).
function addMonths(day: number, n: number): number {
  const d = new Date(day * DAY)
  const y = d.getUTCFullYear()
  const m = d.getUTCMonth() + n
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate()
  return Date.UTC(y, m, Math.min(d.getUTCDate(), last)) / DAY
}
// Moment's date(n): day n of that month, running into the next month when it is past the end.
const setDate = (day: number, n: number): number => {
  const d = new Date(day * DAY)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), n) / DAY
}
const monthOf = (day: number): number => new Date(day * DAY).getUTCMonth()

interface Schedule { type: number; interval: number; range: number; dayOfWeek: number | null; dayOfMonth: number | null; weekOfMonth: number }

// A whole number from min to max; `unset` for no value (missing, blank, or 0 where 0 means none); 'bad'
// for anything else (1.5, -2, 1e9, "abc"), which makes the whole schedule unreadable.
function whole(v: unknown, min: number, max: number, unset: number | null, zeroIsUnset = false): number | null | 'bad' {
  if (v === undefined || v === null || v === '') return unset
  const n = num(v)
  if (n === null || !Number.isInteger(n)) return 'bad'
  if (zeroIsUnset && n === 0) return unset
  return n >= min && n <= max ? n : 'bad'
}

// The job's schedule, every field checked (Astra, 2 Oct: a weekday of 1.5 or a huge interval must never
// spin the page). null: a field is not a value Squeegee writes, so how often is left blank and nothing
// is booked from it.
function scheduleOf(job: Obj): Schedule | null {
  const dowRaw = Array.isArray(job.frequencyDayOfWeek) ? job.frequencyDayOfWeek[0] : job.frequencyDayOfWeek
  const interval = whole(job.frequencyInterval, 0, 520, 0)
  const type = whole(job.frequencyType, 0, 99, -1)
  const range = whole(job.frequencyRange, 0, 52, 0)
  const dayOfWeek = whole(dowRaw, 1, 7, null, true)
  const dayOfMonth = whole(job.frequencyDayOfMonth, 1, 31, null, true)
  const weekOfMonth = whole(job.frequencyWeekOfMonth, 0, 5, 0)
  if (interval === 'bad' || type === 'bad' || range === 'bad' || dayOfWeek === 'bad' || dayOfMonth === 'bad' || weekOfMonth === 'bad') return null
  return {
    // No interval is ad hoc (Squeegee's Frequency constructor).
    type: interval! > 0 ? type! : 0,
    interval: interval!,
    range: range!,
    dayOfWeek,
    dayOfMonth,
    weekOfMonth: weekOfMonth!,
  }
}

// The first day from `day` on that is the weekday wanted (at most 6 days on).
function onWeekday(day: number, want: number): number {
  for (let i = 0; i < 7; i++) if (weekday(day + i) === want) return day + i
  return day
}

// The next date on the schedule after `day` (Squeegee's getMovingNextDate), or null when it does not repeat.
function nextOnSchedule(s: Schedule, day: number): number | null {
  switch (s.type) {
    case 1:
      return day + s.interval
    case 2:
      return (s.dayOfWeek ? onWeekday(day, s.dayOfWeek) : day) + 7 * s.interval
    case 3: {
      const d = addMonths(day, s.interval)
      return s.dayOfMonth ? setDate(d, s.dayOfMonth) : d
    }
    case 4: {
      const start = addMonths(setDate(day, 1), s.interval)
      const month = monthOf(start)
      let d = onWeekday(start, s.dayOfWeek ?? weekday(day)) + 7 * s.weekOfMonth
      for (let i = 0; i < 6 && monthOf(d) !== month; i++) d -= 7
      return d
    }
    default:
      return null
  }
}

const LAST_DAY = Date.UTC(2101, 0, 1) / DAY

// The job's Next Due: the first date on its schedule from today on. A schedule that does not repeat
// has only its first date, so a past one gives none; an unreadable schedule gives none.
export function nextDue(job: Obj, today: string): string {
  const first = dayOf(text(job.date))
  const now = dayOf(today)
  const s = scheduleOf(job)
  if (first === null || now === null || s === null) return ''
  let d: number | null = first
  // At most one step a day across 2000 to 2100.
  for (let guard = 0; d !== null && d < now; guard++) {
    if (guard > 40_000) return ''
    const next: number | null = nextOnSchedule(s, d)
    d = next !== null && next > d && next < LAST_DAY ? next : null
  }
  return d === null ? '' : isoOf(d)
}

const DAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const ordinal = (n: number): string => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`

// How often, in the words Squeegee's All_Jobs.csv uses ("every 4 weeks on Friday"), which the import
// reads as it reads that file. Blank when Squeegee's setting is not one of its known kinds: the owner is
// asked, never told a guess.
export function frequencyWords(job: Obj): string {
  const s = scheduleOf(job)
  if (!s) return ''
  if (s.type === 0) return 'Ad hoc'
  const unit = s.type === 1 ? 'day' : s.type === 2 ? 'week' : s.type === 3 || s.type === 4 ? 'month' : null
  if (!unit) return ''
  const n = s.interval
  // "every 3 to 5 weeks": not one cycle, so the owner picks it (parseCycle reads no range).
  if (s.range > 0 && n > 1) return `every ${Math.max(1, n - s.range)} to ${n + s.range} ${unit}s`
  const every = n === 1 && unit !== 'day' ? `every ${unit}` : `every ${n} ${unit}${n === 1 ? '' : 's'}`
  if (s.type === 2 && s.dayOfWeek) return `${every} on ${DAYS[s.dayOfWeek]}`
  if (s.type === 3 && s.dayOfMonth) return `${every} on the ${ordinal(s.dayOfMonth)}`
  return every
}

// A month-based job in Squeegee's own words ("every month on the 16th", "every 2 months on the 2nd
// Tuesday"), kept in the customer's notes: Roundly counts months as 28 days and more (parseCycle), so
// the owner can see what it was. Blank for any other kind.
export function monthWords(job: Obj): string {
  const s = scheduleOf(job)
  if (!s || s.range > 0 || (s.type !== 3 && s.type !== 4)) return ''
  const every = s.interval === 1 ? 'every month' : `every ${s.interval} months`
  if (s.type === 3) return s.dayOfMonth ? `${every} on the ${ordinal(s.dayOfMonth)}` : every
  const week = s.weekOfMonth >= 4 ? 'last' : ordinal(s.weekOfMonth + 1)
  return s.dayOfWeek ? `${every} on the ${week} ${DAYS[s.dayOfWeek]}` : every
}

export const addressOf = (v: unknown): string => (isObj(v) ? text(v.addressDescription) : '')
const servicesOf = (job: Obj): string => (Array.isArray(job.services) ? job.services.filter(isObj).map((x) => text(x.description)).filter(Boolean).join(', ') : '')
const priceOf = (job: Obj): string => {
  const p = num(job.price)
  return p === null ? text(job.price) : p.toFixed(2)
}

// Each customer's reference, as Squeegee's All_Jobs.csv gives it under "Customer Ref": the customer's
// `_externalId` (Sarah Collins is CUS29843785 in both the backup and the CSV, 2 Oct 2026), else its `_id`. The
// import puts rows with the same reference at the same address together as one customer, so no two live customers
// may ever end up with the same one, across both kinds (Astra, 2 Oct): an `_externalId` is used only when no other
// customer has it, as an `_externalId` or as an `_id`; every other customer goes by its `_id`, and an `_id` that is
// still taken (Squeegee wrote one twice) gets "#2", "#3" until it is free. Keyed by the customer object itself.
export function squeegeeRefs(objects: Obj[]): Map<Obj, string> {
  const live = objects.filter((o) => o.resourceType === 'customers' && !gone(o) && text(o._id))
  const count = (key: (c: Obj) => string): Map<string, number> => {
    const n = new Map<string, number>()
    for (const c of live) if (key(c)) n.set(key(c), (n.get(key(c)) ?? 0) + 1)
    return n
  }
  const ids = count((c) => text(c._id))
  const externals = count((c) => text(c._externalId))
  const refs = new Map<Obj, string>()
  const taken = new Set<string>()
  for (const c of live) {
    const ext = text(c._externalId)
    if (ext && externals.get(ext) === 1 && (!ids.has(ext) || (ext === text(c._id) && ids.get(ext) === 1))) {
      refs.set(c, ext)
      taken.add(ext)
    }
  }
  for (const c of live) {
    if (refs.has(c)) continue
    let ref = text(c._id)
    for (let n = 2; taken.has(ref); n++) ref = `${text(c._id)}#${n}`
    refs.set(c, ref)
    taken.add(ref)
  }
  return refs
}

// A customer's jobs that still exist (not deleted, not in deletedJobs), each with its id: the live ones,
// which come in as work, and the stopped or archived ones, which do not.
export function customerJobs(c: Obj): { live: Obj[]; off: Obj[] } {
  const deletedJobs = isObj(c.deletedJobs) ? c.deletedJobs : {}
  const jobs = (isObj(c.jobs) ? Object.entries(c.jobs) : [])
    .filter((e): e is [string, Obj] => isObj(e[1]) && !gone(e[1]) && !(e[0] in deletedJobs))
    .map(([id, j]) => ({ ...j, _id: text(j._id) || id }))
  return { live: jobs.filter((j) => !stopped(j) && !archived(j)), off: jobs.filter((j) => stopped(j) || archived(j)) }
}

// The data file's objects (the .sqg's data-*.json, already read as JSON) as All_Jobs.csv rows.
export function backupSheet(data: unknown, today: string): BackupSheet {
  if (!Array.isArray(data)) throw new BackupError('not a Squeegee backup')
  const objects = data.filter(isObj)
  const refs = squeegeeRefs(objects)
  // The latest done visit of each job.
  const lastDone = new Map<string, string>()
  for (const o of objects) {
    if (o.resourceType !== 'joboccurrences' || gone(o) || num(o.status) !== 1) continue
    const jobId = text(o.jobId)
    const when = [o.isoCompletedDate, o.isoPlannedDate, o.isoDueDate].map(text).find((v) => ISO.test(v))?.slice(0, 10)
    if (!jobId || !when || dayOf(when) === null || when > today) continue
    if (!lastDone.has(jobId) || lastDone.get(jobId)! < when) lastDone.set(jobId, when)
  }
  const rows: string[][] = [[...BACKUP_HEADINGS]]
  const notIn: BackupSheet['notIn'] = []
  for (const c of objects) {
    if (c.resourceType !== 'customers' || gone(c)) continue
    const name = text(c.name) || addressOf(c.address)
    // Only stopped ("inactive") and archived customers stay out; any other state, or none, is active in
    // Squeegee too (Astra, 2 Oct).
    if (archived(c)) { notIn.push({ name, reason: 'archived in Squeegee' }); continue }
    if (stopped(c)) { notIn.push({ name, reason: 'stopped in Squeegee' }); continue }
    const { live, off } = customerJobs(c)
    // Stopped and archived jobs do not come in as work; what they were is kept in the customer's notes.
    const offNotes = off.map((j) => `${stopped(j) ? 'Stopped' : 'Archived'} in Squeegee: ${[servicesOf(j) || 'a job', priceOf(j) && `£${priceOf(j)}`, frequencyWords(j)].filter(Boolean).join(', ')}`)
    const own = [refs.get(c) ?? text(c._id), name, addressOf(c.address), text(c.telephoneNumber), text(c.email)]
    const description = typeof c.notes === 'string' ? c.notes.trim() : ''
    const row = (j: Obj | null): string[] => {
      const id = j ? text(j._id) : ''
      const months = j ? monthWords(j) : ''
      const notes = [...(months ? [`In Squeegee: ${months}`] : []), ...offNotes].join('\n')
      return [id, ...own, j ? addressOf(j.location) : '', description, j ? servicesOf(j) : '', j ? frequencyWords(j) : '', j ? priceOf(j) : '', (id && lastDone.get(id)) || '', j ? nextDue(j, today) : '', notes]
    }
    if (live.length === 0) rows.push(row(null))
    else for (const j of live) rows.push(row(j))
  }
  // No done visit anywhere (a new account, like the test one): no Last Done column, so the check screen does
  // not show a "Last visits" card that found nothing.
  const lastAt = rows[0].indexOf('Last Done')
  if (rows.slice(1).every((r) => !r[lastAt])) for (const r of rows) r.splice(lastAt, 1)
  return { rows, notIn }
}

// The data file inside the unpacked backup: data-<date>.json (the other file, user-*.json.encrypted, is
// Squeegee's own and cannot be read).
export function dataFileName(names: string[]): string | null {
  return names.find((n) => /(^|\/)data-[^/]*\.json$/i.test(n)) ?? null
}

// When the backup was made, from its data file's name: data-2026-10-02-18-02-43.json is 18:02:43 UTC
// (Ken Lowe's £10 cash, saved at 19:01:26 British Summer Time, is in the backup made a minute later).
// null for a name not in that form.
export function backupMadeAt(name: string): string | null {
  const m = name.match(/(?:^|\/)data-(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})\.json$/i)
  if (!m) return null
  const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`
  return Number.isNaN(Date.parse(iso)) || new Date(iso).toISOString().slice(0, 19) !== iso.slice(0, 19) ? null : iso
}

// The .sqg file's bytes to its data file's objects, and when the backup was made. Any failure is a
// BackupError carrying BACKUP_UNREADABLE (what went wrong is its cause).
export async function readBackupData(bytes: Uint8Array, unxz: Unxz): Promise<{ data: unknown; madeAt: string | null }> {
  try {
    const files = await readSevenZip(bytes, unxz)
    const name = dataFileName(files.map((f) => f.name))
    if (!name) throw new BackupError('no data file in the backup')
    const json = new TextDecoder('utf-8', { fatal: true }).decode(files.find((f) => f.name === name)!.bytes)
    return { data: JSON.parse(json), madeAt: backupMadeAt(name) }
  } catch (e) {
    throw new BackupError(BACKUP_UNREADABLE, { cause: e })
  }
}

// The .sqg file's bytes to rows: unpacked, its data file read, its customers turned into rows. Any
// failure is a BackupError carrying BACKUP_UNREADABLE (what went wrong is its cause).
export async function readBackupFile(bytes: Uint8Array, unxz: Unxz, today: string): Promise<BackupSheet> {
  const { data } = await readBackupData(bytes, unxz)
  try {
    return backupSheet(data, today)
  } catch (e) {
    throw new BackupError(BACKUP_UNREADABLE, { cause: e })
  }
}
